import React from "react";
import PropTypes from "prop-types";
import { Card, Link, Container, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { RED } from "assets/theme/brandColors";

const Section = ({ title, children }) => (
  <MDBox mb={2}>
    <MDTypography variant="h6" fontWeight="bold" mb={0.5}>
      {title}
    </MDTypography>
    <MDTypography variant="body2" color="text">
      {children}
    </MDTypography>
  </MDBox>
);

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function PrivacyPolicy() {
  const navigate = useNavigate();
  return (
    <MDBox
      sx={{
        background: `linear-gradient(180deg, ${RED} 20%, #fe8361 80%)`,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        py: 3,
      }}
    >
      <Container maxWidth="md">
        <Card
          sx={{
            p: 4,
            backgroundColor: "#fff",
            mx: "auto",
            transform: "scale(0.85)",
            transformOrigin: "top center",
          }}
        >
          <MDBox mb={1} textAlign="center">
            <MDTypography variant="h3" fontWeight="bold" sx={{ color: RED }}>
              Privacy Policy
            </MDTypography>
            <MDTypography variant="caption" color="text">
              Last updated: April 2026
            </MDTypography>
          </MDBox>

          <MDBox
            mb={3}
            p={2}
            sx={{ background: "#faf8f5", borderRadius: 2, borderLeft: `3px solid ${RED}` }}
          >
            <MDTypography variant="body2" color="text">
              <strong>Summary:</strong> We collect only what we need to run your account. We do not
              sell your data. You can request deletion at any time by emailing us.
            </MDTypography>
          </MDBox>

          <Section title="1. Who we are">
            This app is operated by Estìa, a research-stage project. References to
            &quot;Estìa&quot;, &quot;we&quot;, or &quot;us&quot; refer to the Estìa project, not any
            individual. Contact:{" "}
            <Link href="mailto:estiahelp@outlook.com" underline="hover" sx={{ color: RED }}>
              estiahelp@outlook.com
            </Link>
          </Section>

          <Section title="2. What data we collect">
            When you create an account and use Estìa, we collect and store:
            <MDBox component="ul" sx={{ pl: 2, mt: 0.5 }}>
              <li>Your username and email address</li>
              <li>Your hashed password (never stored in plain text)</li>
              <li>Schedules you create and recipes you complete</li>
              <li>Your cookbook — recipes you have saved, rated, or added notes to</li>
              <li>Your goals, pantry items, and any preferences you set</li>
              <li>Your XP, level, and streak data</li>
              <li>Basic usage data — which features you use and when</li>
            </MDBox>
          </Section>

          <Section title="3. What we do not collect">
            We do not collect your real name, phone number, payment details, precise location, or
            any sensitive personal data. We do not build advertising profiles or sell your data to
            any third party.
          </Section>

          <Section title="4. Why we collect it">
            We use your data to:
            <MDBox component="ul" sx={{ pl: 2, mt: 0.5 }}>
              <li>Run your account and personalise your experience</li>
              <li>Generate schedules and grocery lists based on your preferences</li>
              <li>Track your progress and maintain your streak and XP</li>
              <li>Improve the platform during the beta research phase</li>
            </MDBox>
            We do not use your data for advertising of any kind.
          </Section>

          <Section title="5. Legal basis (GDPR)">
            If you are in the European Economic Area, we process your data on the basis of{" "}
            <strong>contract</strong> (Article 6(1)(b) GDPR) — processing is necessary to provide
            the service you signed up for — and <strong>legitimate interests</strong> (Article
            6(1)(f) GDPR) for platform improvement during the beta phase.
          </Section>

          <Section title="6. Data storage and security">
            Your data is stored on secured servers. Passwords are hashed and never stored in plain
            text. We take reasonable technical measures to protect your data, though no system is
            completely secure and we cannot guarantee absolute security.
          </Section>

          <Section title="7. International transfers">
            Estìa is operated from the United States. If you are located in the EEA, your data may
            be transferred to and processed in the US. By creating an account you acknowledge this.
            We take reasonable steps to protect data during any such transfer.
          </Section>

          <Section title="8. How long we keep your data">
            We retain your account data for as long as your account is active. If you request
            deletion, we will remove your personal data within 30 days, except where we are required
            to retain it for legal reasons.
          </Section>

          <Section title="9. Your rights">
            If you are in the EEA you have the right to access, correct, delete, or export your
            data, and to object to processing. To exercise any of these rights email us at{" "}
            <Link href="mailto:estiahelp@outlook.com" underline="hover" sx={{ color: RED }}>
              estiahelp@outlook.com
            </Link>
            . We will respond within 30 days. You also have the right to lodge a complaint with your
            local data protection authority, or with the Irish Data Protection Commission at{" "}
            <Link
              href="https://www.dataprotection.ie"
              target="_blank"
              rel="noopener"
              underline="hover"
              sx={{ color: RED }}
            >
              dataprotection.ie
            </Link>
            .
          </Section>

          <Section title="10. Third-party services">
            Estìa uses PostHog (EU cloud, Frankfurt) for anonymous analytics on the landing website.
            The app itself does not use third-party analytics. We do not integrate advertising
            networks, social login, or tracking pixels of any kind.
          </Section>

          <Section title="11. Children">
            Estìa is not directed at children under 16. If you are under 16, please do not create an
            account. If we become aware that a user is under 16 we will delete their account.
          </Section>

          <Section title="12. Changes to this policy">
            If we make material changes to how we handle your data, we will notify you by email or
            via an in-app notice before the changes take effect. The date at the top of this page
            reflects the most recent update.
          </Section>

          <Section title="13. Contact">
            Any questions about this policy:{" "}
            <Link href="mailto:estiahelp@outlook.com" underline="hover" sx={{ color: RED }}>
              estiahelp@outlook.com
            </Link>
          </Section>

          <MDBox textAlign="center" mt={3} mb={1}>
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: RED,
                "&:hover": { backgroundColor: "#fe8361" },
                fontWeight: "bold",
                color: "#fff",
                px: 6,
                py: 1.5,
                fontSize: "1rem",
              }}
              onClick={() => navigate("/authentication/sign-up")}
            >
              Back to Sign Up
            </Button>
          </MDBox>
        </Card>
      </Container>
    </MDBox>
  );
}

export default PrivacyPolicy;
